import { useMutation } from "@tanstack/react-query";
import { Modal } from "react-bootstrap";
import Button from "@/components/Button";
import { useDocumentApi } from "@/hooks/useApi";
import {
  getArchivedDocumentListOptions,
  getProcessingDocumentsOptions,
} from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type { DocumentClient } from "@/lib/types";
import { toast } from "../ui/toast";

type Props = {
  showDeleteConfirm: boolean;
  setShowDeleteConfirm: (show: boolean) => void;
  document: DocumentClient;
};
export default function ConfirmDeleteDocumentModal(props: Props) {
  const documentApi = useDocumentApi();
  const deleteDocumentMutation = useMutation({
    mutationFn: async () => {
      return documentApi.documentDelete({ documentId: props.document.id });
    },
    onSuccess: () => {
      props.setShowDeleteConfirm(false);
      toast.success(`${props.document.name} was deleted`);
    },
    onError: () => {
      toast.error(`Error deleting ${props.document.name}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries(getProcessingDocumentsOptions(documentApi));
      queryClient.invalidateQueries(
        getArchivedDocumentListOptions(documentApi),
      );
    },
  });
  return (
    <Modal
      data-testid="confirm-delete-document-modal"
      show={props.showDeleteConfirm}
      title="Delete Document"
      onClose={() => props.setShowDeleteConfirm(false)}
    >
      <div className="p-3">
        <h2 className="fw-bold">Permanently Delete</h2>
        <div>Please confirm you wish to permanently delete this Document.</div>
        <div className="fw-bold text-danger">This action cannot be undone.</div>
        <div className="d-flex justify-content-end gap-2 pt-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => props.setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={deleteDocumentMutation.isPending}
            onClick={() => deleteDocumentMutation.mutate()}
          >
            {deleteDocumentMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
